import { useRef, useState, useEffect } from "react";
import axios from "axios";
import { Pen, RotateCcw, Check, X } from "lucide-react";
import { toast } from "@/lib/toast";
import { API } from "@/api";
import { getPortalToken } from "@/lib/portalSession";


/**
 * Iter100 — Modal signature canvas pour portail client.
 * Props:
 *  - documentId : id du document a signer
 *  - documentTitle : nom affichable
 *  - onSigned(result) : callback apres signature reussie
 *  - onClose()
 */
export default function SignatureCanvasModal({ documentId, documentTitle, onSigned, onClose }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#072354";
    // Fond blanc explicite (sinon PNG transparent)
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    const isTouch = e.touches && e.touches.length > 0;
    const x = (isTouch ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (isTouch ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x: x * sx, y: y * sy };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasContent(true);
  };

  const end = () => setDrawing(false);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasContent(false);
  };

  const submit = async () => {
    if (!hasContent) {
      toast.error("Veuillez signer avant de valider");
      return;
    }
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const token = getPortalToken() || localStorage.getItem("token");
      const r = await axios.post(
        `${API}/signature/${documentId}`,
        { signature_data: dataUrl },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Document signé");
      onSigned?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de signature");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="signature-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Pen className="w-5 h-5 text-cyan-600" />
            <div>
              <h3 className="font-bold text-gray-900">Signer le document</h3>
              {documentTitle && <p className="text-xs text-gray-500">{documentTitle}</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Fermer">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <p className="text-xs text-gray-500 mb-3">
          Signez dans le cadre ci-dessous avec votre souris ou votre doigt.
        </p>

        <div className="border-2 border-dashed border-cyan-300 rounded-lg overflow-hidden bg-white">
          <canvas
            ref={canvasRef}
            width={600}
            height={200}
            className="w-full touch-none cursor-crosshair"
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={end}
            onMouseLeave={end}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={end}
            data-testid="signature-canvas"
          />
        </div>

        <div className="flex items-center gap-2 mt-4">
          <button
            onClick={clear}
            disabled={submitting}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50 transition flex items-center gap-1.5"
            data-testid="signature-clear-btn"
          >
            <RotateCcw className="w-4 h-4" /> Effacer
          </button>
          <div className="flex-1" />
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50 transition"
            data-testid="signature-cancel-btn"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={!hasContent || submitting}
            className="px-4 py-2 rounded-lg bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-700 disabled:opacity-50 transition flex items-center gap-1.5"
            data-testid="signature-submit-btn"
          >
            <Check className="w-4 h-4" />
            {submitting ? "Envoi..." : "Valider la signature"}
          </button>
        </div>
      </div>
    </div>
  );
}
